// src/components/InsightsEngine.js
import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { insightQueries, sentimentQueries } from '../repository/graphQueries';
import { formatDate, formatDateTime } from '../utils';

// The InsightsEngine component automatically extracts actionable insights from the Neo4j graph
const InsightsEngine = ({ clientId, neo4jService }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [insights, setInsights] = useState([]);
  const [sentimentTrend, setSentimentTrend] = useState([]);
  const [timeRange, setTimeRange] = useState(30); // Default to 30 days

  // Load insights when the component mounts or when the client changes
  useEffect(() => {
    if (!clientId || !neo4jService) return;
    
    const loadInsights = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Get sentiment data for trend visualization
        const sentimentData = await getSentimentData(clientId);
        setSentimentTrend(sentimentData);
        
        // Generate insights from the graph
        const insightResults = await generateInsights(clientId);
        setInsights(insightResults);
      } catch (error) {
        console.error('Error loading insights:', error);
        setError(`Failed to load insights: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    loadInsights();
  }, [clientId, neo4jService, timeRange]);
  
  // Get sentiment data trend from Neo4j
  const getSentimentData = async (clientId) => {
    try {
      const sentimentResults = await neo4jService.runQuery(sentimentQueries.getSentimentOverTime, { 
        clientId
      });
      
      // Format the results for the chart
      return sentimentResults.map(result => ({
        date: formatDate(result.day),
        sentiment: typeof result.avgSentiment === 'number' ? 
          result.avgSentiment : 
          parseFloat(result.avgSentiment) || 0
      }));
    } catch (error) {
      console.error('Error getting sentiment data:', error);
      // Return empty array if there's an error
      return [];
    }
  };
  
  // Generate insights from Neo4j graph data
  const generateInsights = async (clientId) => {
    const insights = [];
    
    try {
      // Check for declining sentiment trend
      const decliningTrendResults = await neo4jService.runQuery(insightQueries.getDecliningTrend, { 
        clientId 
      });
      
      if (decliningTrendResults.length > 0) {
        // Get recent sentiment data to show the decline
        const recentTrendResults = await neo4jService.runQuery(sentimentQueries.getRecentSentimentTrend, { 
          clientId 
        });
        
        const currentSentiment = recentTrendResults.length > 0 && recentTrendResults[0].sentiments.length > 0 
          ? recentTrendResults[0].sentiments[recentTrendResults[0].sentiments.length - 1] 
          : 0;
          
        const previousSentiment = recentTrendResults.length > 0 && recentTrendResults[0].sentiments.length > 1 
          ? recentTrendResults[0].sentiments[recentTrendResults[0].sentiments.length - 3] 
          : 0;
        
        insights.push({
          id: 'sentiment-decline',
          type: 'warning',
          title: 'Declining Sentiment Detected',
          description: 'Client sentiment has declined over the last 3 communication touchpoints.',
          recommendation: 'Schedule a check-in call to address potential concerns.',
          priority: 'high',
          metrics: {
            currentSentiment: Number(currentSentiment.toFixed(2)),
            previousSentiment: Number(previousSentiment.toFixed(2)),
            changePct: Number((((currentSentiment - previousSentiment) / previousSentiment) * 100).toFixed(0))
          }
        });
      }
      
      // Check for blocked initiatives
      const blockedInitiativesResults = await neo4jService.runQuery(insightQueries.getLongBlockedInitiatives, { 
        clientId 
      });
      
      if (blockedInitiativesResults.length > 0) {
        for (const blocked of blockedInitiativesResults) {
          insights.push({
            id: `blocked-${blocked.initiative.replace(/\s+/g, '-').toLowerCase()}`,
            type: 'alert',
            title: 'Critical Initiative Blocked',
            description: `The "${blocked.initiative}" initiative has been blocked by a ${blocked.severity} severity issue for ${blocked.blockingDays}+ days.`,
            recommendation: 'Escalate the blocking issue to the appropriate team and schedule a resolution meeting.',
            priority: 'high',
            metrics: {
              daysBlocked: blocked.blockingDays,
              severity: blocked.severity,
              blockingIssue: blocked.blockingIssue
            }
          });
        }
      }
      
      // Get overdue action items
      const overdueResults = await neo4jService.runQuery(
        `MATCH (a:ActionItem {client_id: $clientId, status: 'pending'})
         WHERE a.deadline < date()
         WITH a.assigned_to as assignee, count(a) as count, 
              avg(duration.between(a.deadline, date()).days) as avgDaysOverdue,
              max(duration.between(a.deadline, date()).days) as maxDaysOverdue
         WHERE count > 1 AND assignee IS NOT NULL
         RETURN assignee, count, avgDaysOverdue, maxDaysOverdue
         ORDER BY count DESC`, 
        { clientId }
      );
      
      if (overdueResults.length > 0) {
        for (const overdue of overdueResults) {
          insights.push({
            id: `overdue-${overdue.assignee.replace(/\s+/g, '-').toLowerCase()}`,
            type: 'alert',
            title: 'Multiple Overdue Action Items',
            description: `${overdue.count} action items assigned to ${overdue.assignee} are past their deadline.`,
            recommendation: `Follow up with ${overdue.assignee} to determine if they need additional support or if deadlines need to be adjusted.`,
            priority: 'medium',
            metrics: {
              overdueCount: overdue.count,
              averageDaysOverdue: Math.round(overdue.avgDaysOverdue),
              maxDaysOverdue: Math.round(overdue.maxDaysOverdue)
            }
          });
        }
      }
      
      // Get goals at risk
      const goalsAtRiskResults = await neo4jService.runQuery(
        `MATCH (g:Goal {client_id: $clientId, status: 'active'})
         OPTIONAL MATCH (i:Initiative)-[:SUPPORTS_GOAL]->(g)
         OPTIONAL MATCH (issue:Issue)-[:BLOCKS]->(i)
         WITH g, count(distinct i) as initiativeCount, 
             count(distinct issue) as blockingIssuesCount,
             duration.between(date(), g.target_date).days as daysToDeadline
         WHERE blockingIssuesCount > 0
         RETURN g.name as goalName, g.target_date as targetDate,
             initiativeCount, blockingIssuesCount,
             daysToDeadline,
             (blockingIssuesCount * 1.0 / initiativeCount) as riskScore
         ORDER BY riskScore DESC
         LIMIT 5`,
        { clientId }
      );
      
      if (goalsAtRiskResults.length > 0) {
        for (const goal of goalsAtRiskResults) {
          if (goal.riskScore >= 0.25) { // Only add if risk score is significant
            insights.push({
              id: `goal-risk-${goal.goalName.replace(/\s+/g, '-').toLowerCase()}`,
              type: 'warning',
              title: 'Goal at Risk',
              description: `The "${goal.goalName}" goal has a high risk score based on blocked initiatives and timeline.`,
              recommendation: 'Review all initiatives supporting this goal and create a recovery plan.',
              priority: goal.riskScore > 0.4 ? 'high' : 'medium',
              metrics: {
                riskScore: Number(goal.riskScore.toFixed(2)),
                daysToDeadline: goal.daysToDeadline,
                blockedInitiatives: goal.blockingIssuesCount,
                totalInitiatives: goal.initiativeCount
              }
            });
          }
        }
      }
      
      // Staff focus analysis
      const staffFocusResults = await neo4jService.runQuery(insightQueries.getStaffFocusDistribution, { 
        clientId 
      });
      
      if (staffFocusResults.length > 0) {
        // Find staff members with imbalanced focus
        for (const staff of staffFocusResults) {
          const totalItems = staff.goalCount + staff.initiativeCount + 
                            staff.actionItemCount + staff.issueCount;
          
          // Check if the staff member is missing engagement with goals but has many initiatives
          if (staff.goalCount === 0 && staff.initiativeCount > 2) {
            insights.push({
              id: `staff-focus-${staff.staff.replace(/\s+/g, '-').toLowerCase()}`,
              type: 'insight',
              title: 'Staff Focus Analysis',
              description: `${staff.staff} is engaged across ${staff.initiativeCount} initiatives but has no involvement with any goals.`,
              recommendation: 'Consider realigning staff assignments to ensure priority goals have adequate support.',
              priority: 'low',
              metrics: {
                involvedInitiatives: staff.initiativeCount,
                actionItems: staff.actionItemCount,
                missingGoalAssociation: 'Yes'
              }
            });
          }
        }
      }
      
      // Add insights for initiatives without action items
      const unaddressedInitiativesResults = await neo4jService.runQuery(insightQueries.getUnaddressedInitiatives, { 
        clientId 
      });
      
      if (unaddressedInitiativesResults.length > 0) {
        for (const initiative of unaddressedInitiativesResults) {
          insights.push({
            id: `unaddressed-${initiative.initiativeName.replace(/\s+/g, '-').toLowerCase()}`,
            type: 'insight',
            title: 'Initiative Lacking Action Items',
            description: `The "${initiative.initiativeName}" initiative has no action items defined.`,
            recommendation: 'Create specific action items for this initiative to ensure progress.',
            priority: 'medium',
            metrics: {
              actionItemCount: 0,
              status: 'Needs attention'
            }
          });
        }
      }
      
      // Add insights for approaching deadlines
      const approachingDeadlinesResults = await neo4jService.runQuery(insightQueries.getApproachingDeadlines, { 
        clientId 
      });
      
      if (approachingDeadlinesResults.length > 0) {
        for (const goal of approachingDeadlinesResults) {
          if (goal.daysRemaining < 14) { // Two weeks or less
            const priority = goal.daysRemaining < 7 ? 'high' : 'medium';
            
            insights.push({
              id: `deadline-${goal.goalName.replace(/\s+/g, '-').toLowerCase()}`,
              type: 'warning',
              title: 'Goal Deadline Approaching',
              description: `The "${goal.goalName}" goal deadline is approaching in ${goal.daysRemaining} days.`,
              recommendation: 'Review goal progress and ensure all supporting initiatives are on track.',
              priority,
              metrics: {
                daysRemaining: goal.daysRemaining,
                initiativeCount: goal.initiativeCount,
                blockingIssues: goal.blockingIssuesCount
              }
            });
          }
        }
      }
      
    } catch (error) {
      console.error('Error generating insights:', error);
    }
    
    return insights;
  };
  
  // Get the appropriate icon and color for each insight type
  const getInsightTypeStyles = (type) => {
    switch (type) {
      case 'alert':
        return {
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          ),
          bgColor: 'bg-red-50',
          textColor: 'text-red-800',
          borderColor: 'border-red-200'
        };
      case 'warning':
        return {
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          bgColor: 'bg-yellow-50',
          textColor: 'text-yellow-800',
          borderColor: 'border-yellow-200'
        };
      case 'insight':
      default:
        return {
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          bgColor: 'bg-blue-50',
          textColor: 'text-blue-800',
          borderColor: 'border-blue-200'
        };
    }
  };
  
  // Format metrics for display
  const formatMetricValue = (key, value) => {
    if (typeof value !== 'number' && typeof value !== 'string') {
      return 'N/A';
    }
    
    if (key.toLowerCase().includes('pct') || key.toLowerCase().includes('percentage')) {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      return `${numValue}%`;
    }
    
    if (key.toLowerCase().includes('sentiment')) {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      return `${(numValue * 100).toFixed(0)}%`;
    }
    
    if (key.toLowerCase().includes('days')) {
      return `${value} days`;
    }
    
    if (key.toLowerCase().includes('score') && typeof value === 'number') {
      if (value <= 1) {
        return `${(value * 100).toFixed(0)}%`;
      }
      return value.toString();
    }
    
    return value.toString();
  };
  
  // Handle time range change
  const handleTimeRangeChange = (e) => {
    setTimeRange(parseInt(e.target.value, 10));
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
        <span className="ml-2 text-gray-700">Analyzing graph data...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">
              {error}
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // If no insights were found
  if (insights.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Insights Available</h3>
        <p className="text-gray-600">
          No actionable insights were detected for this client. Continue monitoring as more data becomes available.
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Time range selector */}
      <div className="flex items-center mb-4">
        <label className="mr-2 text-sm font-medium text-gray-700">Time Range:</label>
        <select 
          className="border border-gray-300 rounded-md p-2"
          value={timeRange}
          onChange={handleTimeRangeChange}
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={180}>Last 6 months</option>
        </select>
      </div>
      
      {/* Sentiment trend chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Client Sentiment Trend</h3>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={sentimentTrend.map(item => ({
              ...item,
              // Format date if needed
              date: formatDate(item.date)
            }))}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              tick={{fontSize: 12}}
              tickFormatter={(value) => {
                try {
                  const date = new Date(value);
                  if (!isNaN(date.getTime())) {
                    return `${date.getMonth()+1}/${date.getDate()}`;
                  }
                  return value;
                } catch (e) {
                  console.error('Date parsing error:', e);
                  return value;
                }
              }}
            />
            <YAxis 
              domain={[0, 1]} 
              tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
            />
            <Tooltip 
              formatter={(value) => [`${(value * 100).toFixed(1)}%`, 'Sentiment']}
              labelFormatter={(value) => `Date: ${value}`}
            />
            <Line 
              type="monotone" 
              dataKey="sentiment" 
              stroke="#2563EB" 
              strokeWidth={2} 
              dot={false}
              activeDot={{r: 6}}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      {/* Insights list */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">AI-Generated Insights</h3>
        
        {insights.map(insight => {
          const { icon, bgColor, textColor, borderColor } = getInsightTypeStyles(insight.type);
          
          return (
            <div 
              key={insight.id}
              className={`rounded-lg border p-4 ${bgColor} ${borderColor}`}
            >
              <div className="flex items-start">
                <div className={`flex-shrink-0 ${textColor}`}>{icon}</div>
                <div className="ml-3 flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className={`text-lg font-medium ${textColor}`}>{insight.title}</h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      insight.priority === 'high' ? 'bg-red-100 text-red-800' :
                      insight.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {insight.priority.charAt(0).toUpperCase() + insight.priority.slice(1)} Priority
                    </span>
                  </div>
                  
                  <p className={`mt-1 text-sm ${textColor}`}>{insight.description}</p>
                  
                  {/* Metrics */}
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.entries(insight.metrics).map(([key, value], index) => (
                      <div key={index} className="bg-white rounded px-3 py-2 border border-gray-200">
                        <p className="text-xs text-gray-500">{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</p>
                        <p className="text-sm font-medium">
                          {formatMetricValue(key, value)}
                        </p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Recommendation */}
                  <div className="mt-4 bg-white p-3 rounded-md border border-gray-200">
                    <h5 className="text-xs font-medium text-gray-700 mb-1">Recommended Action</h5>
                    <p className="text-sm text-gray-900">{insight.recommendation}</p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InsightsEngine;